/**
 * Catalog of common AWS services and the IAM action bundles StratoNext grants the operator roles for
 * each. The onboarding wizard's "Role permissions" step lets the customer pick services once; the
 * selected bundles are baked into the generated CloudFormation template (Operations role gets
 * `read`, the Operations Max ceiling role gets `read` + `write`).
 *
 * This is a deliberately small, most-used starting set — not exhaustive. Customers can restrict or
 * expand permissions in their own IAM console anytime. Action strings must match the backend's
 * `^[\w:*-]+$` validation (see the setup-template resolver).
 */
export interface AwsServiceDef {
	key: string;
	label: string;
	/** Read-only actions (Operations role, and part of the ceiling). */
	read: string[];
	/** Additional mutating actions layered on for the Operations Max ceiling role. */
	write: string[];
	/** Shown as a default card in the picker (and selected by default). */
	popular?: boolean;
}

export const AWS_SERVICE_CATALOG: AwsServiceDef[] = [
	{
		key: "s3",
		label: "S3",
		popular: true,
		read: ["s3:Get*", "s3:List*", "s3:Describe*"],
		write: [
			"s3:CreateBucket",
			"s3:PutObject",
			"s3:DeleteObject",
			"s3:RestoreObject",
		],
	},
	{
		key: "cloudwatch",
		label: "CloudWatch",
		popular: true,
		read: ["cloudwatch:Get*", "cloudwatch:List*", "cloudwatch:Describe*"],
		write: [
			"cloudwatch:PutMetricAlarm",
			"cloudwatch:DeleteAlarms",
			"cloudwatch:SetAlarmState",
		],
	},
	{
		key: "lambda",
		label: "Lambda",
		popular: true,
		read: ["lambda:Get*", "lambda:List*"],
		write: [
			"lambda:UpdateFunctionConfiguration",
			"lambda:PublishVersion",
			"lambda:PutFunctionConcurrency",
			"lambda:DeleteFunctionConcurrency",
		],
	},
	{
		key: "ec2",
		label: "EC2",
		popular: true,
		read: ["ec2:Describe*"],
		write: [
			"ec2:StartInstances",
			"ec2:StopInstances",
			"ec2:RebootInstances",
			"ec2:ModifyInstanceAttribute",
		],
	},
	{
		key: "cost-explorer",
		label: "Cost Explorer",
		popular: true,
		// Read-only cost/usage insights; Cost Explorer has no mutating actions we grant.
		read: ["ce:Get*", "ce:Describe*", "ce:List*"],
		write: [],
	},
	{
		key: "cognito",
		label: "Cognito",
		// Read-only; feeds the "Cognito users" signal. No mutating actions granted.
		read: ["cognito-idp:Describe*", "cognito-idp:List*"],
		write: [],
	},
	{
		key: "logs",
		label: "CloudWatch Logs",
		read: [
			"logs:Describe*",
			"logs:List*",
			"logs:Get*",
			"logs:FilterLogEvents",
			"logs:StartQuery",
			"logs:StopQuery",
		],
		write: ["logs:CreateLogGroup", "logs:PutRetentionPolicy"],
	},
	{
		key: "dynamodb",
		label: "DynamoDB",
		read: [
			"dynamodb:Describe*",
			"dynamodb:List*",
			"dynamodb:GetItem",
			"dynamodb:BatchGetItem",
			"dynamodb:Query",
			"dynamodb:Scan",
		],
		write: [
			"dynamodb:PutItem",
			"dynamodb:UpdateItem",
			"dynamodb:DeleteItem",
			"dynamodb:BatchWriteItem",
		],
	},
	{
		key: "rds",
		label: "RDS",
		read: ["rds:Describe*", "rds:ListTagsForResource"],
		write: [
			"rds:RebootDBInstance",
			"rds:StartDBInstance",
			"rds:StopDBInstance",
			"rds:ModifyDBInstance",
		],
	},
	{
		key: "ecs",
		label: "ECS",
		read: ["ecs:Describe*", "ecs:List*"],
		write: [
			"ecs:UpdateService",
			"ecs:UpdateServicePrimaryTaskSet",
			"ecs:UpdateTaskSet",
			"ecs:StopTask",
		],
	},
	{
		key: "eks",
		label: "EKS",
		read: ["eks:Describe*", "eks:List*", "eks:AccessKubernetesApi"],
		write: ["eks:UpdateNodegroupConfig", "eks:UpdateClusterConfig"],
	},
	{
		key: "sns",
		label: "SNS",
		read: ["sns:Get*", "sns:List*"],
		write: ["sns:Publish"],
	},
	{
		key: "sqs",
		label: "SQS",
		read: ["sqs:Get*", "sqs:List*"],
		write: ["sqs:SendMessage", "sqs:DeleteMessage", "sqs:PurgeQueue"],
	},
	{
		key: "ecr",
		label: "ECR",
		read: [
			"ecr:Describe*",
			"ecr:List*",
			"ecr:GetDownloadUrlForLayer",
			"ecr:BatchGetImage",
		],
		write: ["ecr:PutImage", "ecr:BatchDeleteImage"],
	},
	{
		key: "stepfunctions",
		label: "Step Functions",
		read: ["states:Describe*", "states:List*", "states:GetExecutionHistory"],
		write: ["states:StartExecution", "states:StopExecution"],
	},
];

/** Service keys shown/selected by default (the popular ones). */
export const DEFAULT_SERVICE_KEYS: string[] = AWS_SERVICE_CATALOG.filter(
	(s) => s.popular,
).map((s) => s.key);

const CATALOG_BY_KEY = new Map(AWS_SERVICE_CATALOG.map((s) => [s.key, s]));

/** Picker value entries for user-typed "Other" actions are stored as `custom:<iam-action>`. */
export const CUSTOM_ACTION_PREFIX = "custom:";

/**
 * Resolves selected service keys into a flat, de-duplicated, stable-ordered IAM action list.
 * `read` yields read-only actions; `readwrite` adds each service's write actions on top.
 *
 * Hand-typed "Other" actions land in `readwrite` only. They used to be granted verbatim to both,
 * which quietly put an arbitrary action into the baseline role — the one an agent uses with no
 * approval at all. Now they can be requested and approved, never used unattended.
 */
export function resolveServiceActions(
	keys: string[],
	variant: "read" | "readwrite",
): string[] {
	const seen = new Set<string>();
	for (const key of keys) {
		if (key.startsWith(CUSTOM_ACTION_PREFIX)) {
			if (variant === "readwrite") {
				seen.add(key.slice(CUSTOM_ACTION_PREFIX.length));
			}
			continue;
		}
		const svc = CATALOG_BY_KEY.get(key);
		if (!svc) continue;
		for (const action of svc.read) seen.add(action);
		if (variant === "readwrite")
			for (const action of svc.write) seen.add(action);
	}
	return [...seen];
}
